'use client';

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { PlanningModel } from '~/hooks/usePlanningModel';

export interface PendingLinearAttachment {
  id: string;
  identifier: string;
  title: string;
}

export interface PendingImageAttachment {
  id: string;
  file: File;
}

export interface PendingFileAttachment {
  id: string;
  file: File;
}

export interface PendingChatMessage {
  prompt: string;
  linearAttachments: PendingLinearAttachment[];
  imageAttachments: PendingImageAttachment[];
  fileAttachments?: PendingFileAttachment[];
  selectedModel?: PlanningModel;
  enableWebSearch?: boolean;
}

interface PendingChatContextType {
  pendingMessage: PendingChatMessage | null;
  setPendingMessage: (message: PendingChatMessage) => void;
  consumePendingMessage: () => PendingChatMessage | null;
  clearPendingMessage: () => void;
}

const PendingChatContext = createContext<PendingChatContextType | undefined>(undefined);

export const PendingChatProvider = ({ children }: { children: ReactNode }) => {
  const [pendingMessage, setPendingMessageState] = useState<PendingChatMessage | null>(null);

  const setPendingMessage = useCallback((message: PendingChatMessage) => {
    setPendingMessageState(message);
  }, []);

  const consumePendingMessage = useCallback(() => {
    const message = pendingMessage;
    setPendingMessageState(null);
    return message;
  }, [pendingMessage]);

  const clearPendingMessage = useCallback(() => {
    setPendingMessageState(null);
  }, []);

  return (
    <PendingChatContext.Provider value={{ 
      pendingMessage, 
      setPendingMessage, 
      consumePendingMessage,
      clearPendingMessage 
    }}>
      {children}
    </PendingChatContext.Provider>
  );
}

export function usePendingChat() {
  const context = useContext(PendingChatContext);
  if (!context) {
    throw new Error('usePendingChat must be used within PendingChatProvider');
  }
  return context;
}
